import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Grid,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Box,
  CircularProgress,
  Alert,
  TextField,
  Button,
  Dialog,
  DialogContent
} from '@mui/material';
import format from 'date-fns/format';
import Page from '../../components/Page';
import tracker from '../../api/tracker';

// Helper function to format dates (e.g., "May 8, 2025, 9:00:00 AM")
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    };
    return new Date(dateString).toLocaleString(undefined, options); // Uses browser's default locale
  } catch (error) {
    console.error("Error formatting date:", dateString, error);
    return "Invalid Date";
  }
};
const formatFullDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    };
    // Usando 'es-DO' para español de República Dominicana. Ajusta si es necesario.
    return new Date(dateString).toLocaleString('es-DO', options);
  } catch (error) {
    console.error("Error formatting full date time:", dateString, error);
    return "Fecha Inválida";
  }
};

// NUEVA FUNCIÓN: Helper function to get a readable date (e.g., "8 de mayo de 2025")
const formatReadableDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const options = {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    };
    // Usando 'es-DO' para español de República Dominicana.
    // Esto producirá un formato como "8 de mayo de 2025".
    return date.toLocaleDateString('es-DO', options);
  } catch (error) {
    console.error("Error formatting readable date:", dateString, error);
    return "Fecha Inválida";
  }
};

export default function AttendancePhotosPage() {
  const theme = useTheme();
  const { id } = useParams(); // constructionSite or employee ID
  const navigate = useNavigate();

  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsError, setRecordsError] = useState(null);
  const [filterDate, setFilterDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [searchQuery, setSearchQuery] = useState('');
  const [openImage, setOpenImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [constructionSiteName, setConstructionSiteName] = useState('');

  const fetchAttendanceData = useCallback(async () => {
    setRecordsLoading(true);
    setRecordsError(null);
    const endpoint = `attendance/attendancePhotosFiltered/${id}/${filterDate}${searchQuery ? `?search=${encodeURIComponent(searchQuery)}&locale=es` : ''}`;
    console.log(`Fetching from ${endpoint}`);
    try {
      const result = await tracker.get(endpoint);
      if (result.data && result.data.data.length > 0) {
        setConstructionSiteName(result.data.data[0].construction_site);
        setAttendanceRecords(result.data.data);
      } else {
        setAttendanceRecords([]);
        setConstructionSiteName('');
        console.warn(`No data found for id: ${id}.`);
      }
    } catch (err) {
      setRecordsError(err.message || `Failed to fetch data for id ${id}.`);
      setAttendanceRecords([]);
    } finally {
      setRecordsLoading(false);
    }
  }, [id, filterDate, searchQuery]);

  // Fetch attendance data when component mounts or dependencies change
  useEffect(() => {
    fetchAttendanceData();
  }, [fetchAttendanceData]);

  return (
    <Page title="Registro Fotográfico de Asistencia">
      <Container maxWidth={false} disableGutters>
        {/* FILTER CARD */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            {/* Back link */}
            <Typography variant="body2" color="primary" sx={{ cursor: 'pointer', mb: 1 }} onClick={() => navigate(-1)}>
              Volver atrás
            </Typography>
            <Typography variant="h5" gutterBottom>
              Registros fotograficos de asistencia para obra: {constructionSiteName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                label="Fecha"
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Buscar obrero"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') fetchAttendanceData(); }}
              />

            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            {recordsLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Cargando registros...</Typography>
              </Box>
            )}

            {recordsError && <Alert severity="error" sx={{mt: 2}}>Error: {recordsError}</Alert>}

            {!recordsLoading && !recordsError && attendanceRecords.length === 0 && (
              <Alert severity="info" sx={{mt: 2}}>No se encontraron registros de asistencia.</Alert>
            )}

            {!recordsLoading && !recordsError && attendanceRecords.length > 0 && (
              <Grid container spacing={4}>
                {attendanceRecords.map((record) => (
                  <Grid item xs={12} key={record.id}>
                    <Card elevation={3}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${theme.palette.divider}`, pb: 1, mb: 2 }}>
                          <Typography variant="subtitle1" gutterBottom>
                            <strong>Empleado:</strong> {record.employee_name || 'No especificado'}
                          </Typography>
                          <Typography variant="subtitle1" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                            {formatReadableDate(record.star_date)}
                          </Typography>
                        </Box>

                        <Grid container spacing={3} sx={{mt: 1}}>
                          {/* Clock In Photo and Details */}
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" fontWeight="bold" color="primary.main" gutterBottom>
                              Foto de Entrada
                            </Typography>
                            {record.attendance_photo ? (
                              <CardMedia
                                component="img"
                                height="300"
                                image={record.attendance_photo}
                                alt={`Foto de entrada para ID ${record.id}`}
                                onClick={() => { setSelectedImage(record.attendance_photo); setOpenImage(true); }}
                                sx={{ cursor: 'pointer', border: `1px solid ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius, objectFit: 'contain', mb: 1 }}
                              />
                            ) : (
                              <Box height={300} display="flex" alignItems="center" justifyContent="center" sx={{ border: `2px dashed ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius, backgroundColor: theme.palette.action.hover, mb: 1 }}>
                                <Typography variant="caption">Foto de entrada no disponible</Typography>
                              </Box>
                            )}
                            <Typography variant="body2" color="text.secondary">
                              <strong>Hora de Entrada:</strong> {formatDate(record.star_date)}
                            </Typography>
                          </Grid>

                          {/* Clock Out Photo and Details */}
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" fontWeight="bold" color="secondary.main" gutterBottom>
                              Foto de Salida
                            </Typography>
                            {record.attendance_photo_exit ? (
                              <CardMedia
                                component="img"
                                height="300"
                                image={record.attendance_photo_exit}
                                alt={`Foto de salida para ID ${record.id}`}
                                onClick={() => { setSelectedImage(record.attendance_photo_exit); setOpenImage(true); }}
                                sx={{ cursor: 'pointer', border: `1px solid ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius, objectFit: 'contain', mb: 1 }}
                              />
                            ) : (
                              <Box height={300} display="flex" alignItems="center" justifyContent="center" sx={{ border: `2px dashed ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius, backgroundColor: theme.palette.action.hover, mb: 1 }}>
                                <Typography variant="caption">
                                  {record.end_date ? 'Foto de salida no disponible' : 'Aún no ha marcado salida'}
                                </Typography>
                              </Box>
                            )}
                            <Typography variant="body2" color="text.secondary">
                              <strong>Hora de Salida:</strong> {formatDate(record.end_date)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Container>
      <Dialog open={openImage} onClose={() => setOpenImage(false)} maxWidth="lg">
        <DialogContent sx={{ p: 0 }}>
          <Box component="img" src={selectedImage} alt="Vista ampliada" sx={{ width: '100%', height: 'auto' }} />
        </DialogContent>
      </Dialog>
    </Page>
  );
}